import { useState, useEffect, useCallback } from 'react';
import type { TourStep } from '@/components/ui/product-tour';

const TOUR_STORAGE_KEY = 'revaluation-tour-completed';

export function useRevaluationTour() {
  const [isTourOpen, setIsTourOpen] = useState(false);
  const [hasCompletedTour, setHasCompletedTour] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(completed === 'true');
  }, []);

  const startTour = useCallback(() => {
    setIsTourOpen(true);
  }, []);

  const closeTour = useCallback(() => {
    setIsTourOpen(false);
  }, []);

  const completeTour = useCallback(() => {
    localStorage.setItem(TOUR_STORAGE_KEY, 'true');
    setHasCompletedTour(true);
    setIsTourOpen(false);
  }, []);

  const resetTour = useCallback(() => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    setHasCompletedTour(false);
  }, []);

  const tourSteps: TourStep[] = [
    {
      target: '[data-tour="revaluation-header"]',
      title: 'Welcome to Asset Revaluation',
      content: 'This is where you revalue assets to reflect their current market value. Revaluations adjust the carrying value of assets based on professional valuations or market conditions.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="revaluation-cards"]',
      title: 'Revaluation Overview',
      content: 'These cards provide a quick overview of your revaluation activity: Net Revaluation Adjustments for the year, total Assets Revalued across all batches, and any batches Pending Approval.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="start-batch-button"]',
      title: 'Start New Revaluation Batch',
      content: 'Click here to begin a new revaluation batch. Select assets and specify the revaluation amounts or percentages to apply.',
      placement: 'left',
    },
    {
      target: '[data-tour="view-summary-button"]',
      title: 'View Last Batch Summary',
      content: 'Review the details of your most recently completed revaluation batch, including the total revaluation impact on your asset register.',
      placement: 'left',
    },
    {
      target: '[data-tour="search-transactions"]',
      title: 'Search Transactions',
      content: 'Search through your revaluation transaction history to find specific batches by reference, date, or revaluation details.',
      placement: 'bottom',
    },
    {
      target: '[data-tour="transaction-history"]',
      title: 'Transaction History',
      content: 'View all your revaluation batches with details like date, total revaluation amount, number of assets affected, and status.',
      placement: 'top',
    },
    {
      target: '[data-tour="revaluation-impact"]',
      title: 'Revaluation Impact',
      content: 'Each batch shows the total revaluation impact - increases or decreases to asset values. This helps track how revaluations affect your overall asset register value.',
      placement: 'left',
    },
  ];

  return {
    isTourOpen,
    hasCompletedTour,
    tourSteps,
    startTour,
    closeTour,
    completeTour,
    resetTour,
  };
}
